import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useMemo, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { useCreateInvoice, useCustomerOrders } from "@/api/invoices";
import { CreateInvoiceSchema, createInvoiceSchema } from "@/api.schemas";
import { CustomerSearch } from "@/features/customers/customer-search";

export function CreateInvoiceForm() {
  const [currentPage, setCurrentPage] = useState(1);
  const ORDERS_PER_PAGE = 100;

  // Form initialization
  const form = useForm<CreateInvoiceSchema>({
    resolver: zodResolver(createInvoiceSchema),
    defaultValues: {
      customerId: "",
      orderIds: [],
      dateRange: {
        from: new Date(),
        to: new Date(),
      },
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Default to 7 days from now
      discount: undefined,
    },
  });

  // Fetch orders for selected customer and date range with pagination
  const { data: ordersResponse, isLoading: isLoadingOrders } =
    useCustomerOrders(
      form.watch("customerId"),
      form.watch("dateRange"),
      currentPage,
      ORDERS_PER_PAGE
    );

  // Extract orders and pagination metadata
  const orders = ordersResponse?.data ?? [];
  const totalPages = ordersResponse?.metadata?.totalPages ?? 1;
  const totalCount = ordersResponse?.metadata?.totalCount ?? 0;

  // Ensure current page is valid when total pages changes
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Check if all orders on the current page are selected
  const areAllCurrentPageOrdersSelected = useMemo(() => {
    if (!orders || orders.length === 0) return false;
    return orders.every((order) => form.watch("orderIds").includes(order.id));
  }, [orders, form.watch("orderIds")]);

  // Handle select all checkbox change
  const handleSelectAllChange = (checked: boolean) => {
    const currentOrders = form.watch("orderIds");

    if (checked) {
      // Add all current page orders that aren't already selected
      const newOrderIds = [...currentOrders];
      orders.forEach((order) => {
        if (!currentOrders.includes(order.id)) {
          newOrderIds.push(order.id);
        }
      });
      form.setValue("orderIds", newOrderIds);
    } else {
      // Remove all current page orders
      const orderIdsToKeep = currentOrders.filter(
        (id) => !orders.some((order) => order.id === id)
      );
      form.setValue("orderIds", orderIdsToKeep);
    }
  };

  // Keep track of selected orders and their totals
  const [selectedOrderTotals, setSelectedOrderTotals] = useState<
    Record<string, number>
  >({});

  // Update selected order totals when orders change
  useEffect(() => {
    if (orders.length > 0) {
      const newTotals = { ...selectedOrderTotals };

      // Add or update totals for orders on the current page
      orders.forEach((order) => {
        newTotals[order.id] =
          (order.totalFees ?? 0) + (order.manualPricingAdjustment ?? 0);
      });

      setSelectedOrderTotals(newTotals);
    }
  }, [orders]);

  // Calculate total amount for selected orders
  const selectedOrdersTotal = useMemo(() => {
    const selectedIds = form.watch("orderIds");

    // Sum up the totals of selected orders
    return selectedIds.reduce((total, orderId) => {
      return total + (selectedOrderTotals[orderId] || 0);
    }, 0);
  }, [form.watch("orderIds"), selectedOrderTotals]);

  // Calculate final total with discount
  const finalTotal = useMemo(() => {
    const discount = form.watch("discount");
    if (!discount) return selectedOrdersTotal;

    if (discount.type === "percentage") {
      return selectedOrdersTotal * (1 - discount.value / 100);
    } else {
      return Math.max(0, selectedOrdersTotal - discount.value);
    }
  }, [selectedOrdersTotal, form.watch("discount")]);

  // Create invoice mutation
  const createInvoiceMutation = useCreateInvoice();

  const onSubmit = form.handleSubmit((values) => {
    // Convert dueDate to EST/EDT timezone
    const dueDate = new Date(values.dueDate);
    dueDate.setUTCHours(23, 59, 59, 999);

    createInvoiceMutation.mutate({
      ...values,
      dueDate, // Pass the Date object directly
    });
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-8">
        <div>
          <div className="space-y-6">
            {/* Customer Selection */}
            <FormField
              control={form.control}
              name="customerId"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Customer</FormLabel>
                  <CustomerSearch
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      // Reset page to 1 when customer changes
                      setCurrentPage(1);
                      // Clear selected orders when customer changes
                      form.setValue("orderIds", []);
                    }}
                    placeholder="Select customer..."
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date Range Selection */}
            <FormField
              control={form.control}
              name="dateRange"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Date Range</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-[300px] justify-start text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value?.from && field.value?.to ? (
                            <>
                              {format(field.value.from, "LLL dd, y")} -{" "}
                              {format(field.value.to, "LLL dd, y")}
                            </>
                          ) : (
                            <span>Pick a date range</span>
                          )}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        selected={{
                          from: field.value.from,
                          to: field.value.to,
                        }}
                        onSelect={(range) => {
                          field.onChange(range);
                          // Reset page to 1 when date range changes
                          setCurrentPage(1);
                        }}
                        numberOfMonths={2}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Due Date Selection */}
            <FormField
              control={form.control}
              name="dueDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Due Date</FormLabel>
                  <FormLabel className="font-normal text-muted-foreground text-sm">
                    If not specified, invoice will be due in 7 days
                  </FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-[240px] justify-start text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a due date</span>
                          )}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={
                          (date) => date < new Date() // Can't select past dates
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Discount Selection */}
            <FormField
              control={form.control}
              name="discount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Discount (Optional)</FormLabel>
                  <div className="flex space-x-4">
                    <Select
                      onValueChange={(value) => {
                        if (value === "none") {
                          field.onChange(undefined);
                        } else {
                          field.onChange({
                            type: value as "percentage" | "amount",
                            value: 0,
                          });
                        }
                      }}
                      value={field.value ? field.value.type : "none"}
                    >
                      <FormControl>
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Select discount type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No Discount</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="amount">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>

                    {field.value && (
                      <div className="flex items-center space-x-2">
                        <Input
                          type="number"
                          min={0}
                          max={field.value.type === "percentage" ? 100 : 1000}
                          step={field.value.type === "percentage" ? 1 : 0.01}
                          value={field.value.value}
                          onChange={(e) => {
                            field.onChange({
                              ...field.value,
                              value: parseFloat(e.target.value),
                            });
                          }}
                          className="w-[120px]"
                        />
                        <span>
                          {field.value.type === "percentage" ? "%" : "$"}
                        </span>
                      </div>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Orders Table */}
            {!isLoadingOrders && (
              <div>
                <FormLabel>Select Orders</FormLabel>
                {orders && orders.length > 0 ? (
                  <>
                    <div className="max-h-[700px] overflow-y-auto border rounded-md">
                      <Table>
                        <TableHeader className="sticky top-0 bg-white z-10">
                          <TableRow>
                            <TableHead className="w-[50px]">
                              <Checkbox
                                checked={areAllCurrentPageOrdersSelected}
                                onCheckedChange={handleSelectAllChange}
                                aria-label="Select all orders"
                              />
                            </TableHead>
                            <TableHead>Order Number</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Total Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {orders.map((order) => (
                            <TableRow key={order.id}>
                              <TableCell>
                                <Checkbox
                                  checked={form
                                    .watch("orderIds")
                                    .includes(order.id)}
                                  onCheckedChange={(checked) => {
                                    const currentOrders =
                                      form.watch("orderIds");
                                    if (checked) {
                                      form.setValue("orderIds", [
                                        ...currentOrders,
                                        order.id,
                                      ]);
                                    } else {
                                      form.setValue(
                                        "orderIds",
                                        currentOrders.filter(
                                          (id) => id !== order.id
                                        )
                                      );
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>#{order.orderNumber}</TableCell>
                              <TableCell>
                                {format(new Date(order.createdAt), "LLL dd, y")}
                              </TableCell>
                              <TableCell>
                                $
                                {(
                                  (order.totalFees ?? 0) +
                                  (order.manualPricingAdjustment ?? 0)
                                ).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination Controls */}
                    {orders && orders.length > 0 && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-muted-foreground">
                          Showing {(currentPage - 1) * ORDERS_PER_PAGE + 1} to{" "}
                          {Math.min(currentPage * ORDERS_PER_PAGE, totalCount)}{" "}
                          of {totalCount} orders
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCurrentPage((prev) => Math.max(prev - 1, 1))
                            }
                            disabled={currentPage === 1}
                            type="button"
                          >
                            <ChevronLeftIcon className="h-4 w-4" />
                          </Button>
                          <div className="text-sm">
                            Page {currentPage} of {totalPages}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages)
                              )
                            }
                            disabled={currentPage === totalPages}
                            type="button"
                          >
                            <ChevronRightIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Total Amount Display */}
                    <div className="mt-4 text-right space-y-2">
                      <p className="text-lg">
                        Subtotal: ${selectedOrdersTotal.toFixed(2)}
                      </p>
                      {form.watch("discount") && (
                        <p className="text-lg text-red-600">
                          Discount: -$
                          {(selectedOrdersTotal - finalTotal).toFixed(2)}
                        </p>
                      )}
                      <p className="text-lg font-semibold">
                        Final Total: ${finalTotal.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500">
                        Selected Orders: {form.watch("orderIds").length}
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground mt-2">
                    {isLoadingOrders
                      ? "Loading orders..."
                      : "No invoiceable orders found in this time period."}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <Button type="submit">Create Invoice</Button>
      </form>
    </Form>
  );
}
